import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/Button/Button";
import Popup from "../../components/Popup/Popup";
import Loading from "../../components/Loading/Loading";
import {
    fetchAdminEmployees,
    updateAdminEmployee,
    updateEmployeeRoles,
    clearAdminUpdateFeedback,
} from "../../redux/adminSlice";
import { hasRole } from "../../utils/api";
import "./Administrative.css";

const SIZE_OPTIONS = ["XS", "S", "M", "L", "XL", "XXL"];

const STATUS_OPTIONS = ["Active", "Inactive"];

const WORKDAY_OPTIONS = ["Full-time", "Part-time", "Consultant"];

const ROLE_OPTIONS = [
    { key: "admin", label: "Admin" },
    { key: "hr", label: "RRHH" },
    { key: "marketing", label: "Marketing" },
    { key: "games", label: "Games" },
];

const SOCIAL_FIELDS = [
    { key: "linkedin", label: "LinkedIn" },
    { key: "github", label: "GitHub" },
    { key: "gitlab", label: "GitLab" },
    { key: "instagram", label: "Instagram" },
    { key: "x", label: "X (Twitter)" },
    { key: "facebook", label: "Facebook" },
    { key: "tiktok", label: "TikTok" },
    { key: "artstation", label: "ArtStation" },
];

const normalizeDateInput = (value) => {
    if (!value) return "";
    // If value already contains YYYY-MM-DD, use it directly (avoids timezone shifts)
    const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
};

const buildEditForm = (employee) => ({
    _id: employee?._id || "",
    status: employee?.status || "",
    hireDate: normalizeDateInput(employee?.hireDate),
    birthdayDate: normalizeDateInput(employee?.birthdayDate),
    name: employee?.name || "",
    nickname: employee?.nickname || "",
    workday: employee?.workday || "",
    role: employee?.role || "",
    documentId: employee?.documentId || "",
    internalEmail: employee?.internalEmail || "",
    eps: employee?.eps || "",
    binanceEmail: employee?.binanceEmail || "",
    metamaskWallet: employee?.metamaskWallet || "",
    discordId: employee?.discordId || "",
    description: employee?.description || "",
    imageProfile: employee?.imageProfile || "",
    roles: getEmployeeRoles(employee),
    salary: employee?.salary ?? "",
    size: {
        sweatshirt: employee?.size?.sweatshirt || "",
        tshirt: employee?.size?.tshirt || "",
    },
    socialNetworks: {
        x: employee?.socialNetworks?.x || "",
        instagram: employee?.socialNetworks?.instagram || "",
        facebook: employee?.socialNetworks?.facebook || "",
        tiktok: employee?.socialNetworks?.tiktok || "",
        linkedin: employee?.socialNetworks?.linkedin || "",
        artstation: employee?.socialNetworks?.artstation || "",
        github: employee?.socialNetworks?.github || "",
        gitlab: employee?.socialNetworks?.gitlab || "",
    },
    bankAccounts: employee?.bankAccounts?.map((b) => ({
        bank: b?.bank || "",
        bankAccountNumber: b?.bankAccountNumber || "",
    })) || [],
});

// Roles efectivos: los datos antiguos solo tienen isAdmin.
const getEmployeeRoles = (employee) => {
    const roles = new Set(employee?.roles || []);
    if (employee?.isAdmin) roles.add("admin");
    return ROLE_OPTIONS.map((role) => role.key).filter((key) => roles.has(key));
};

const sameRoles = (a, b) => a.length === b.length && a.every((role) => b.includes(role));

// Solo campos de UpdateEmployeeDto (la API rechaza cualquier campo extra).
// Los opcionales vacíos que la API valida como fecha, URL, email o número se omiten.
const buildEmployeePayload = (form) => {
    const payload = {
        status: form.status,
        workday: form.workday,
        name: form.name.trim(),
        nickname: form.nickname.trim(),
        role: form.role.trim(),
        documentId: form.documentId.trim(),
        internalEmail: form.internalEmail.trim(),
        eps: form.eps.trim(),
        metamaskWallet: form.metamaskWallet.trim(),
        discordId: form.discordId.trim(),
        description: form.description.trim(),
        size: {
            sweatshirt: form.size.sweatshirt,
            tshirt: form.size.tshirt,
        },
        socialNetworks: Object.fromEntries(
            Object.entries(form.socialNetworks).map(([key, value]) => [key, (value || "").trim()])
        ),
        bankAccounts: form.bankAccounts
            .map((bank) => ({
                bank: (bank.bank || "").trim(),
                bankAccountNumber: (bank.bankAccountNumber || "").trim(),
            }))
            .filter((bank) => bank.bank || bank.bankAccountNumber),
    };

    if (form.hireDate) payload.hireDate = form.hireDate;
    if (form.birthdayDate) payload.birthdayDate = form.birthdayDate;
    if (form.binanceEmail.trim()) payload.binanceEmail = form.binanceEmail.trim();
    if (form.imageProfile.trim()) payload.imageProfile = form.imageProfile.trim();
    if (form.salary !== "" && form.salary !== null) payload.salary = Number(form.salary);

    return payload;
};

export default function Administrative() {
    const dispatch = useDispatch();
    const currentEmployee = useSelector((state) => state.auth?.employee);
    const canManageEmployees = hasRole(currentEmployee, "hr");
    const isAdmin = hasRole(currentEmployee, "admin");
    const { employees, employeesStatus, employeesError, updateStatus, updateError } = useSelector(
        (state) => state.admin
    );

    const [activeSection, setActiveSection] = useState("employees");
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [form, setForm] = useState(buildEditForm(null));
    const [openSection, setOpenSection] = useState("personal");

    useEffect(() => {
        if (canManageEmployees && activeSection === "employees" && employeesStatus === "idle") {
            dispatch(fetchAdminEmployees());
        }
    }, [canManageEmployees, activeSection, employeesStatus, dispatch]);

    const openEdit = (employee) => {
        setSelectedEmployee(employee);
        setForm(buildEditForm(employee));
        setOpenSection("personal");
        dispatch(clearAdminUpdateFeedback());
    };

    const closeEdit = () => {
        setSelectedEmployee(null);
        setForm(buildEditForm(null));
        dispatch(clearAdminUpdateFeedback());
    };

    const handleField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleSizeChange = (key, value) => {
        setForm((prev) => ({
            ...prev,
            size: { ...prev.size, [key]: value },
        }));
    };

    const handleSocialChange = (key, value) => {
        setForm((prev) => ({
            ...prev,
            socialNetworks: { ...prev.socialNetworks, [key]: value },
        }));
    };

    const handleBankChange = (index, field, value) => {
        setForm((prev) => {
            const nextBanks = [...prev.bankAccounts];
            nextBanks[index] = { ...nextBanks[index], [field]: value };
            return { ...prev, bankAccounts: nextBanks };
        });
    };

    const addBank = () => {
        setForm((prev) => ({
            ...prev,
            bankAccounts: [...prev.bankAccounts, { bank: "", bankAccountNumber: "" }],
        }));
    };

    const removeBank = (index) => {
        setForm((prev) => ({
            ...prev,
            bankAccounts: prev.bankAccounts.filter((_, i) => i !== index),
        }));
    };

    const handleRoleToggle = (roleKey, checked) => {
        setForm((prev) => ({
            ...prev,
            roles: checked
                ? [...prev.roles.filter((role) => role !== roleKey), roleKey]
                : prev.roles.filter((role) => role !== roleKey),
        }));
    };

    const toggleSection = (sectionId) => {
        setOpenSection((prev) => (prev === sectionId ? null : sectionId));
    };

    const handleSaveEmployee = async () => {
        const payload = buildEmployeePayload(form);

        const result = await dispatch(updateAdminEmployee({ id: form._id, employeeData: payload }));
        if (!updateAdminEmployee.fulfilled.match(result)) return;

        // Los permisos van por un endpoint aparte y solo los cambia un admin.
        if (isAdmin && !sameRoles(form.roles, getEmployeeRoles(selectedEmployee))) {
            const rolesResult = await dispatch(updateEmployeeRoles({ id: form._id, roles: form.roles }));
            if (!updateEmployeeRoles.fulfilled.match(rolesResult)) return;
        }

        closeEdit();
        dispatch(fetchAdminEmployees());
    };

    const renderSection = (sectionId, title, content) => (
        <div className="admin-accordion-item">
            <button
                type="button"
                className={`admin-accordion-header ${openSection === sectionId ? "open" : ""}`}
                onClick={() => toggleSection(sectionId)}
            >
                <h3 className="admin-section-title">{title}</h3>
                <span className="admin-accordion-icon">▼</span>
            </button>
            {openSection === sectionId && <div className="admin-accordion-body">{content}</div>}
        </div>
    );

    if (!canManageEmployees) {
        return (
            <article className="container-home admin-page">
                <h1 className="title">ADMINISTRATIVE<hr /></h1>
                <p className="admin-no-access">No tienes permisos para ver esta sección.</p>
            </article>
        );
    }

    return (
        <article className="container-home admin-page">
            <h1 className="title">ADMINISTRATIVE<hr /></h1>

            <div className="admin-submenu" role="tablist" aria-label="Submenu administrativo">
                <Button
                    type="button"
                    variant="outline"
                    size="small"
                    role="tab"
                    className="admin-submenu-btn"
                    selected={activeSection === "employees"}
                    aria-selected={activeSection === "employees"}
                    onClick={() => setActiveSection("employees")}
                >
                    Empleados
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="small"
                    role="tab"
                    className="admin-submenu-btn"
                    selected={activeSection === "accounting"}
                    aria-selected={activeSection === "accounting"}
                    onClick={() => setActiveSection("accounting")}
                >
                    Contabilidad
                </Button>
            </div>

            {activeSection === "accounting" ? (
                <div className="admin-construction">Contabilidad en construccion</div>
            ) : employeesStatus === "loading" ? (
                <Loading />
            ) : employeesStatus === "failed" ? (
                <p className="admin-error">{employeesError || "Error al cargar empleados"}</p>
            ) : (
                <section className="admin-table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Nickname</th>
                                <th>Workday</th>
                                <th>Role</th>
                                <th>Internal Email</th>
                                <th>Binance Email</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employees.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="admin-empty">No hay empleados.</td>
                                </tr>
                            ) : (
                                employees.map((employee) => (
                                    <tr key={employee._id}>
                                        <td>{employee.name || "-"}</td>
                                        <td>{employee.nickname || "-"}</td>
                                        <td>{employee.workday || "-"}</td>
                                        <td>{employee.role || "-"}</td>
                                        <td>{employee.internalEmail || "-"}</td>
                                        <td>{employee.binanceEmail || "-"}</td>
                                        <td>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="small"
                                                className="admin-edit-btn"
                                                onClick={() => openEdit(employee)}
                                            >
                                                Editar
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </section>
            )}

            <Popup
                isOpen={Boolean(selectedEmployee)}
                title="Editar Empleado"
                onClose={closeEdit}
                onCancel={closeEdit}
                onConfirm={handleSaveEmployee}
                confirmText={updateStatus === "loading" ? "Guardando..." : "Guardar cambios"}
                confirmDisabled={
                    updateStatus === "loading" ||
                    !form.name.trim() ||
                    !form.nickname.trim() ||
                    !form.status ||
                    !form.workday ||
                    !form.role.trim() ||
                    !form.internalEmail.trim()
                }
                cancelDisabled={updateStatus === "loading"}
                maxWidth="760px"
                height="88vh"
            >
                <div className="admin-edit-form">
                    {renderSection(
                        "personal",
                        "Informacion Basica y Perfil",
                        <div className="admin-edit-grid">
                            <label className="admin-field">
                                <span>Estado</span>
                                <select value={form.status} onChange={(e) => handleField("status", e.target.value)}>
                                    <option value="">Seleccionar estado</option>
                                    {STATUS_OPTIONS.map((status) => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="admin-field">
                                <span>Workday</span>
                                <select value={form.workday} onChange={(e) => handleField("workday", e.target.value)}>
                                    <option value="">Seleccionar jornada</option>
                                    {WORKDAY_OPTIONS.map((workday) => (
                                        <option key={workday} value={workday}>{workday}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="admin-field">
                                <span>Role</span>
                                <input value={form.role} onChange={(e) => handleField("role", e.target.value)} />
                            </label>
                            <label className="admin-field">
                                <span>Fecha de ingreso</span>
                                <input
                                    type="date"
                                    value={form.hireDate}
                                    onChange={(e) => handleField("hireDate", e.target.value)}
                                />
                            </label>
                            <label className="admin-field">
                                <span>Salary</span>
                                <input
                                    type="number"
                                    value={form.salary}
                                    onChange={(e) => handleField("salary", e.target.value)}
                                />
                            </label>
                            <label className="admin-field">
                                <span>Internal Email</span>
                                <input
                                    type="email"
                                    value={form.internalEmail}
                                    onChange={(e) => handleField("internalEmail", e.target.value)}
                                />
                            </label>

                            <label className="admin-field">
                                <span>Image Profile URL</span>
                                <input
                                    value={form.imageProfile}
                                    onChange={(e) => handleField("imageProfile", e.target.value)}
                                />
                            </label>
                            {isAdmin && (
                                <div className="admin-field admin-field--full">
                                    <span>Permisos</span>
                                    <div className="admin-roles">
                                        {ROLE_OPTIONS.map((role) => (
                                            <label className="admin-checkbox-label" key={role.key}>
                                                <input
                                                    type="checkbox"
                                                    checked={form.roles.includes(role.key)}
                                                    onChange={(e) => handleRoleToggle(role.key, e.target.checked)}
                                                />
                                                {role.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <label className="admin-field">
                                <span>Nombre</span>
                                <input value={form.name} onChange={(e) => handleField("name", e.target.value)} />
                            </label>

                            <label className="admin-field">
                                <span>Nickname</span>
                                <input value={form.nickname} onChange={(e) => handleField("nickname", e.target.value)} />
                            </label>

                            <label className="admin-field">
                                <span>Fecha de cumpleaños</span>
                                <input
                                    type="date"
                                    value={form.birthdayDate}
                                    onChange={(e) => handleField("birthdayDate", e.target.value)}
                                />
                            </label>

                            <label className="admin-field">
                                <span>Documento</span>
                                <input value={form.documentId} onChange={(e) => handleField("documentId", e.target.value)} />
                            </label>

                            <label className="admin-field">
                                <span>EPS</span>
                                <input value={form.eps} onChange={(e) => handleField("eps", e.target.value)} />
                            </label>


                            <label className="admin-field admin-field--full">
                                <span>Descripcion</span>
                                <textarea
                                    value={form.description}
                                    onChange={(e) => handleField("description", e.target.value)}
                                />
                            </label>

                            <label className="admin-field">
                                <span>Talla Sweatshirt</span>
                                <select
                                    value={form.size.sweatshirt}
                                    onChange={(e) => handleSizeChange("sweatshirt", e.target.value)}
                                >
                                    <option value="">Seleccionar talla</option>
                                    {SIZE_OPTIONS.map((size) => (
                                        <option key={`sw-${size}`} value={size}>{size}</option>
                                    ))}
                                </select>
                            </label>

                            <label className="admin-field">
                                <span>Talla T-Shirt</span>
                                <select
                                    value={form.size.tshirt}
                                    onChange={(e) => handleSizeChange("tshirt", e.target.value)}
                                >
                                    <option value="">Seleccionar talla</option>
                                    {SIZE_OPTIONS.map((size) => (
                                        <option key={`ts-${size}`} value={size}>{size}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    )}

                    {renderSection(
                        "contact",
                        "Formas de Pago",
                        <div className="admin-edit-grid">
                            <label className="admin-field admin-field--full">
                                <span>Binance Email</span>
                                <input
                                    type="email"
                                    value={form.binanceEmail}
                                    onChange={(e) => handleField("binanceEmail", e.target.value)}
                                />
                            </label>

                            <label className="admin-field admin-field--full">
                                <span>Metamask Wallet</span>
                                <input
                                    value={form.metamaskWallet}
                                    onChange={(e) => handleField("metamaskWallet", e.target.value)}
                                />
                            </label>

                            <div className="admin-field admin-field--full">
                                <span>Cuentas Bancarias</span>
                                <div className="admin-banks">
                                    {form.bankAccounts.map((bank, index) => (
                                        <div className="admin-bank-row" key={`bank-${index}`}>
                                            <label className="admin-field">
                                                <span>Banco</span>
                                                <input
                                                    value={bank.bank || ""}
                                                    onChange={(e) => handleBankChange(index, "bank", e.target.value)}
                                                />
                                            </label>

                                            <label className="admin-field">
                                                <span>N. Cuenta</span>
                                                <input
                                                    value={bank.bankAccountNumber || ""}
                                                    onChange={(e) => handleBankChange(index, "bankAccountNumber", e.target.value)}
                                                />
                                            </label>

                                            <button
                                                type="button"
                                                className="admin-remove-btn"
                                                onClick={() => removeBank(index)}
                                            >
                                                X
                                            </button>
                                        </div>
                                    ))}

                                    <button type="button" className="admin-add-btn" onClick={addBank}>
                                        + Agregar banco
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {renderSection(
                        "social",
                        "Redes Sociales",
                        <div className="admin-edit-grid">
                            <label className="admin-field admin-field--full">
                                <span>Discord ID</span>
                                <input value={form.discordId} onChange={(e) => handleField("discordId", e.target.value)} />
                            </label>

                            {SOCIAL_FIELDS.map((social) => (
                                <label className="admin-field admin-field--full" key={social.key}>
                                    <span>{social.label}</span>
                                    <input
                                        value={form.socialNetworks[social.key]}
                                        onChange={(e) => handleSocialChange(social.key, e.target.value)}
                                        placeholder="https://..."
                                    />
                                </label>
                            ))}
                        </div>
                    )}
                </div>
                {updateError && <p className="admin-error">{updateError}</p>}
            </Popup>
        </article>
    );
}
